import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';
import { useRemoveMerchandisingCollectionSlice } from '@/hooks/useRemoveMerchandisingCollectionSlice';
import { MerchandisingCollectionSlice } from '@/services/MerchandisingCollectionsService';

interface ConfirmRemoveSliceModalProps {
  sliceToRemove: MerchandisingCollectionSlice | null;
  onRemove: () => void;
}

export function ConfirmRemoveSliceModal({
  sliceToRemove,
  onRemove,
}: ConfirmRemoveSliceModalProps) {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.MerchandisingCollections.components.ConfirmRemoveSliceModal',
  });

  const { removeMerchandisingCollectionSlice, isLoading } =
    useRemoveMerchandisingCollectionSlice();

  async function handleRemoveCollection() {
    if (!sliceToRemove) {
      return;
    }

    try {
      await removeMerchandisingCollectionSlice({
        collectionId: sliceToRemove.collectionId,
        sliceId: sliceToRemove.id,
      });

      onRemove();
      toast.success(t('toast.success'));
    } catch {
      toast.error(t('toast.error'));
    }
  }

  return (
    <AlertDialog open={!!sliceToRemove} onOpenChange={onRemove}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('title')}</AlertDialogTitle>

          <AlertDialogDescription>
            {t('description', { name: sliceToRemove?.name })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel type="button">
            {t('cancelButtonLabel')}
          </AlertDialogCancel>

          <AlertDialogAction
            type="button"
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={handleRemoveCollection}
          >
            {isLoading ? t('removeButtonLabelLoading') : t('removeButtonLabel')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
